import { useState, useCallback, useMemo } from 'react';
import { useMediaQuery } from '@mui/material';
import { LAUNCHER_GRID, clampGridColumns, clampGridRows } from '../../utils/launcherGrid';

/**
 * Manages all grid-configuration and theme state.
 * Extracted from HomeLauncherPage – behavior is identical.
 */
export default function useLauncherResponsive() {
  const [themePref,          setThemePref]          = useState('system');
  const [gridColumns,        setGridColumns]        = useState(LAUNCHER_GRID.DEFAULT_COLUMNS);
  const [gridRows,           setGridRows]           = useState(LAUNCHER_GRID.DEFAULT_ROWS);
  const [iconSize,           setIconSize]           = useState('medium');
  const [gap,                setGap]                = useState(2);
  const [showGrid,           setShowGrid]           = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');

  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const isDark = themePref === 'dark' || (themePref === 'system' && prefersDark);

  const safeColumns = useMemo(() => clampGridColumns(gridColumns), [gridColumns]);
  const safeRows = useMemo(() => clampGridRows(gridRows), [gridRows]);
  const GRID_MAX = safeColumns * safeRows;

  /** Apply a raw layout data object (from storage or API) to this hook's state. */
  const applyLayoutData = useCallback((data) => {
    if (!data) return;
    setThemePref(data.theme              || 'system');
    setGridColumns(clampGridColumns(data.gridColumns || LAUNCHER_GRID.DEFAULT_COLUMNS));
    setGridRows(clampGridRows(data.gridRows || LAUNCHER_GRID.DEFAULT_ROWS));
    setIconSize(data.iconSize            || 'medium');
    setGap(data.gap                      ?? 2);
    setShowGrid(data.showGrid            || false);
    setBackgroundImageUrl(data.backgroundImageUrl || '');
  }, []);

  /** Return a snapshot of the current responsive config (for persistence). */
  const getConfig = useCallback(() => ({
    theme:              themePref,
    gridColumns:        safeColumns,
    gridRows:           safeRows,
    iconSize,
    gap,
    showGrid,
    backgroundImageUrl,
  }), [themePref, safeColumns, safeRows, iconSize, gap, showGrid, backgroundImageUrl]);

  return {
    themePref, setThemePref,
    gridColumns: safeColumns, setGridColumns,
    gridRows:    safeRows,    setGridRows,
    iconSize,    setIconSize,
    gap,         setGap,
    showGrid,    setShowGrid,
    backgroundImageUrl, setBackgroundImageUrl,
    isDark,
    GRID_MAX,
    applyLayoutData,
    getConfig,
  };
}
